import React, { useCallback, useEffect, useMemo, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from "@mui/material";
import CustomSnackbar from "../../components/CustomSnackbar";
import ConfirmDialog from "../../components/ConfirmDialog";
import Logger from "../../../services/Logger";
import StatsJigsawService from "../../../services/StatsJigsawService";
import AdminJigsawService from "../AdminJigsawService";
import TagMigrationService from "../../system/tag-migration/TagMigrationService";
import StoryImageZoomViewport from "../../story/StoryImageZoomViewport";
import dayjs from "dayjs";

const LOW_PUBLISHED = 5;
const SORT_FIELDS = ["tag", "draftCount", "reviewedCount", "publishedCount", "playCount", "lastPublishedAt"];
const SORT_STORAGE_KEY = "admin.jigsaw.tagStats.sort";
const LANGUAGE_STORAGE_KEY = "admin.jigsaw.tagStats.language";
const KEYWORD_STORAGE_KEY = "admin.jigsaw.tagStats.keyword";
const DEFAULT_SORT = { field: "publishedCount", dir: "asc" };
const RENAME_SOURCES = ["BEDROCK", "WORD_PUZZLE", "JIGSAW", "SLIDING"];
const LANGUAGE_FILTER_OPTIONS = [
    { value: "", label: "All languages" },
    { value: "english", label: "English" },
    { value: "french", label: "French" },
];

/** Likely existing tags to merge leftover names into after the last tag migration. */
const RENAME_HINTS = {
    capital: ["city", "geography", "country", "society", "architecture"],
    culture: ["art", "lifestyle", "society", "education", "book", "music", "history"],
    travel: ["city", "geography", "nature", "country", "landscape"],
};

function groupedRenameOptions(fromTag, allTags) {
    const from = String(fromTag || "").trim().toLowerCase();
    const others = (allTags || []).filter((t) => t && String(t).trim().toLowerCase() !== from);
    const hints = RENAME_HINTS[from] || [];
    const hintSet = new Set(hints.map((h) => h.toLowerCase()));
    const byLower = new Map(others.map((t) => [String(t).toLowerCase(), t]));
    const suggested = hints.map((h) => byLower.get(h.toLowerCase())).filter(Boolean);
    const rest = others
        .filter((t) => !hintSet.has(String(t).toLowerCase()))
        .slice()
        .sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: "base" }));
    return [
        ...suggested.map((tag) => ({ tag, group: "Suggested" })),
        ...rest.map((tag) => ({ tag, group: "Existing tags" })),
    ];
}

function formatCount(value) {
    return Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "—";
}

function formatDateTime(epochMillis) {
    return epochMillis ? dayjs(Number(epochMillis)).format("YYYY-MM-DD HH:mm") : "—";
}

function getStoryTitle(story, preferredLanguage) {
    if (!story) return "";
    const en = story.english?.title ?? story.en?.title ?? "";
    const fr = story.french?.title ?? story.fr?.title ?? "";
    const pref = typeof preferredLanguage === "string" ? preferredLanguage.trim().toLowerCase() : "";
    if (pref === "french") return fr || en || "";
    if (pref === "english") return en || fr || "";
    return en || fr || "";
}

function getPublishStoryId(group) {
    return group?.story?.id ?? group?.storyId ?? null;
}

function getPublishTargetImageUrl(group) {
    const story = group?.story;
    const fromStory =
        story?.imageUrl
        ?? story?.imageIcon400Url
        ?? story?.imageIcon200Url
        ?? story?.imagePreviewUrl
        ?? null;
    if (fromStory) {
        return fromStory;
    }
    const templates = Array.isArray(group?.templates) ? group.templates : [];
    for (const tpl of templates) {
        const url = tpl?.previewImageUrl ?? tpl?.preview_image_url ?? tpl?.imageUrl ?? tpl?.image_url;
        if (typeof url === "string" && url.trim()) {
            return url;
        }
    }
    return "";
}

function PublishTargetPreview({ group, languageFilter, tag }) {
    const imageUrl = getPublishTargetImageUrl(group);
    const title = getStoryTitle(group?.story, languageFilter);
    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            {title ? (
                <Typography variant="subtitle2" sx={{ mb: 1, flexShrink: 0 }}>
                    {title}
                </Typography>
            ) : null}
            <StoryImageZoomViewport
                imageUrl={imageUrl}
                fillHeight
                emptyLabel={tag ? `No image for ${tag}.` : "No image for this puzzle."}
                sx={{ flex: 1, minHeight: 0 }}
            />
        </Box>
    );
}

function lastPublishedValue(row) {
    const raw = row?.lastPublishedAt;
    if (raw == null || raw === "") {
        return null;
    }
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) {
        return n;
    }
    const parsed = dayjs(raw);
    return parsed.isValid() ? parsed.valueOf() : null;
}

function loadStoredSort() {
    try {
        const raw = window.localStorage.getItem(SORT_STORAGE_KEY);
        if (!raw) {
            return DEFAULT_SORT;
        }
        const parsed = JSON.parse(raw);
        const field = SORT_FIELDS.includes(parsed?.field) ? parsed.field : DEFAULT_SORT.field;
        const dir = parsed?.dir === "desc" ? "desc" : "asc";
        return { field, dir };
    } catch {
        return DEFAULT_SORT;
    }
}

function persistSort(field, dir) {
    try {
        window.localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify({ field, dir }));
    } catch {
        /* private mode */
    }
}

function loadLanguageFilter(fallback) {
    try {
        const v = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (v === "" || v === "english" || v === "french") return v;
    } catch {
        /* private mode */
    }
    if (fallback === "" || fallback === "english" || fallback === "french") return fallback;
    return "english";
}

function loadStoredKeyword() {
    try {
        const v = window.localStorage.getItem(KEYWORD_STORAGE_KEY);
        return typeof v === "string" ? v : "";
    } catch {
        /* private mode */
    }
    return "";
}

/**
 * Per-tag REVIEWED vs PUBLISHED jigsaw story counts, with publish-one for thin tags
 * and user play sessions on the last column.
 */
export default function JigsawTagStatsPanel({ language }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState({ draftTotal: 0, reviewedTotal: 0, publishedTotal: 0, playTotal: 0 });
    const [languageFilter, setLanguageFilter] = useState(() => loadLanguageFilter(language));
    const [keyword, setKeyword] = useState(loadStoredKeyword);
    const [sort, setSort] = useState(loadStoredSort);
    const sortField = sort.field;
    const sortDir = sort.dir;
    const [publishTag, setPublishTag] = useState(null);
    const [publishTarget, setPublishTarget] = useState({ loading: false, group: null, error: "" });
    const [publishPeekNonce, setPublishPeekNonce] = useState(0);
    const [publishing, setPublishing] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [renameDrafts, setRenameDrafts] = useState({});
    const [renameConfirm, setRenameConfirm] = useState(null);
    const [renaming, setRenaming] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await StatsJigsawService.statsByTag(languageFilter || undefined);
            const list = Array.isArray(result?.tags) ? result.tags : [];
            setRows(list);
            setRenameDrafts({});
            setTotals({
                draftTotal: Number(result?.draftTotal) || 0,
                reviewedTotal: Number(result?.reviewedTotal) || 0,
                publishedTotal: Number(result?.publishedTotal) || 0,
                playTotal: Number(result?.playTotal) || 0,
            });
        } catch (err) {
            Logger.error("Failed to load jigsaw tag stats:", err);
            setError("Failed to load tag statistics.");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [languageFilter]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!publishTag) {
            setPublishTarget({ loading: false, group: null, error: "" });
            return undefined;
        }
        let cancelled = false;
        setPublishTarget({ loading: true, group: null, error: "" });
        (async () => {
            const result = await AdminJigsawService.peekPublishNext(languageFilter || undefined, publishTag);
            if (cancelled) {
                return;
            }
            if (!result.success || !result.data) {
                setPublishTarget({
                    loading: false,
                    group: null,
                    error: result.error || "No REVIEWED image found for this tag.",
                });
                return;
            }
            setPublishTarget({ loading: false, group: result.data, error: "" });
        })();
        return () => {
            cancelled = true;
        };
    }, [publishTag, languageFilter, publishPeekNonce]);

    const handleSort = (field) => {
        if (!SORT_FIELDS.includes(field)) return;
        const nextDir =
            sortField === field
                ? (sortDir === "asc" ? "desc" : "asc")
                : (field === "lastPublishedAt" ? "desc" : "asc");
        setSort({ field, dir: nextDir });
        persistSort(field, nextDir);
    };

    const visibleRows = useMemo(() => {
        const q = keyword.trim().toLowerCase();
        const filtered = q
            ? rows.filter((row) => String(row.tag || "").toLowerCase().includes(q))
            : rows.slice();
        const dir = sortDir === "asc" ? 1 : -1;
        filtered.sort((a, b) => {
            if (sortField === "tag") {
                return dir * String(a.tag || "").localeCompare(String(b.tag || ""), undefined, {
                    sensitivity: "base",
                });
            }
            if (sortField === "lastPublishedAt") {
                const av = lastPublishedValue(a);
                const bv = lastPublishedValue(b);
                if (av == null && bv == null) {
                    return String(a.tag || "").localeCompare(String(b.tag || ""), undefined, {
                        sensitivity: "base",
                    });
                }
                if (av == null) return 1;
                if (bv == null) return -1;
                if (av !== bv) return dir * (av - bv);
                return String(a.tag || "").localeCompare(String(b.tag || ""), undefined, {
                    sensitivity: "base",
                });
            }
            const av = Number(a[sortField]) || 0;
            const bv = Number(b[sortField]) || 0;
            if (av !== bv) return dir * (av - bv);
            return String(a.tag || "").localeCompare(String(b.tag || ""), undefined, {
                sensitivity: "base",
            });
        });
        return filtered;
    }, [keyword, rows, sortDir, sortField]);

    const allTagNames = useMemo(
        () => rows.map((row) => String(row.tag || "").trim()).filter(Boolean),
        [rows],
    );

    const handlePublish = async () => {
        if (!publishTag || publishing || publishTarget.loading) return;
        setPublishing(true);
        try {
            const storyId =
                publishTarget.group?.story?.id
                ?? publishTarget.group?.storyId
                ?? null;
            if (storyId) {
                const result = await AdminJigsawService.publishStory(storyId);
                if (!result.success) {
                    setSnackbar({
                        open: true,
                        severity: "error",
                        message: result.error || "Publish failed",
                    });
                    return;
                }
                setSnackbar({
                    open: true,
                    severity: "success",
                    message: `Published jigsaw for “${publishTag}”.`,
                });
                setPublishTag(null);
                await load();
                return;
            }

            const result = await AdminJigsawService.publishReviewed(1, languageFilter || undefined, publishTag);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Publish failed",
                });
                return;
            }
            const count = result.data?.publishedCount ?? 0;
            setSnackbar({
                open: true,
                severity: count > 0 ? "success" : "warning",
                message:
                    count > 0
                        ? `Published ${count} jigsaw for “${publishTag}”.`
                        : `No REVIEWED jigsaw left for “${publishTag}”.`,
            });
            setPublishTag(null);
            await load();
        } catch (err) {
            Logger.error("Failed to publish jigsaw by tag:", err);
            setSnackbar({
                open: true,
                severity: "error",
                message: err?.message || "Publish failed",
            });
        } finally {
            setPublishing(false);
        }
    };

    const closePublishDialog = () => {
        if (publishing || deleting) return;
        setDeleteConfirmOpen(false);
        setPublishTag(null);
    };

    const handleDeleteStory = async (storyId) => {
        if (!storyId || deleting || publishing) return;
        setDeleting(true);
        try {
            const result = await AdminJigsawService.removeByStoryId(storyId);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Delete failed",
                });
                return;
            }
            setSnackbar({
                open: true,
                severity: "success",
                message: `Deleted jigsaw for “${publishTag}”.`,
            });
            await load();
            setPublishPeekNonce((n) => n + 1);
        } catch (err) {
            Logger.error("Failed to delete jigsaw by tag:", err);
            setSnackbar({
                open: true,
                severity: "error",
                message: err?.message || "Delete failed",
            });
        } finally {
            setDeleting(false);
        }
    };

    const handleRename = async () => {
        const from = String(renameConfirm?.from || "").trim();
        const to = String(renameConfirm?.to || "").trim();
        if (!from || !to || renaming) return;
        setRenaming(true);
        try {
            const result = await TagMigrationService.rename(from, to, RENAME_SOURCES);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Rename failed",
                });
                return;
            }
            const total = Number(result.data?.updated?.total) || 0;
            setSnackbar({
                open: true,
                severity: "success",
                message:
                    total === 1
                        ? `Renamed “${from}” to “${to}” (1 record).`
                        : `Renamed “${from}” to “${to}” (${total} records).`,
            });
            setRenameConfirm(null);
            await load();
        } catch (err) {
            Logger.error("Failed to rename jigsaw tag:", err);
            setSnackbar({
                open: true,
                severity: "error",
                message: err?.message || "Rename failed",
            });
        } finally {
            setRenaming(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    const publishStoryId = getPublishStoryId(publishTarget.group);
    const publishTitle = getStoryTitle(publishTarget.group?.story, languageFilter);

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Each row is one tag on a jigsaw story image. <strong>Draft</strong> is still in progress.
                <strong> Reviewed</strong> is the ready-to-go-live inventory. <strong>Published</strong> is live
                in the app. Publishing one reviewed image also queues a Jigsaw Muse <strong>push</strong> (batched;
                sends only when server push is on). <strong>Last published</strong> is the most recent
                live bundle for that tag. <strong>Plays</strong> is how many user sessions
                have started that tag (guests included; test and staff excluded).
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <Typography variant="body2">
                    Tags: <strong>{formatCount(rows.length)}</strong>
                </Typography>
                <Typography variant="body2">
                    Draft: <strong>{formatCount(totals.draftTotal)}</strong>
                </Typography>
                <Typography variant="body2">
                    Reviewed: <strong>{formatCount(totals.reviewedTotal)}</strong>
                </Typography>
                <Typography variant="body2">
                    Published: <strong>{formatCount(totals.publishedTotal)}</strong>
                </Typography>
                <Typography variant="body2">
                    Plays: <strong>{formatCount(totals.playTotal)}</strong>
                </Typography>
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="jigsaw-tag-stats-language-label">Language</InputLabel>
                    <Select
                        labelId="jigsaw-tag-stats-language-label"
                        value={languageFilter}
                        label="Language"
                        onChange={(event) => {
                            const next = event.target.value;
                            setLanguageFilter(next);
                            try {
                                window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
                            } catch {
                                /* private mode */
                            }
                        }}
                    >
                        {LANGUAGE_FILTER_OPTIONS.map((option) => (
                            <MenuItem key={option.value || "all"} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    label="Filter tags"
                    value={keyword}
                    onChange={(event) => {
                        const next = event.target.value;
                        setKeyword(next);
                        try {
                            window.localStorage.setItem(KEYWORD_STORAGE_KEY, next);
                        } catch {
                            /* private mode */
                        }
                    }}
                    sx={{ maxWidth: 320, flex: "1 1 220px" }}
                />
            </Stack>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === "tag"}
                                    direction={sortField === "tag" ? sortDir : "asc"}
                                    onClick={() => handleSort("tag")}
                                >
                                    Tag
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === "draftCount"}
                                    direction={sortField === "draftCount" ? sortDir : "asc"}
                                    onClick={() => handleSort("draftCount")}
                                >
                                    Draft
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === "reviewedCount"}
                                    direction={sortField === "reviewedCount" ? sortDir : "asc"}
                                    onClick={() => handleSort("reviewedCount")}
                                >
                                    Reviewed
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === "publishedCount"}
                                    direction={sortField === "publishedCount" ? sortDir : "asc"}
                                    onClick={() => handleSort("publishedCount")}
                                >
                                    Published
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === "playCount"}
                                    direction={sortField === "playCount" ? sortDir : "asc"}
                                    onClick={() => handleSort("playCount")}
                                >
                                    Plays
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === "lastPublishedAt"}
                                    direction={sortField === "lastPublishedAt" ? sortDir : "desc"}
                                    onClick={() => handleSort("lastPublishedAt")}
                                >
                                    Last published
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">Publish</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        No tags found.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            visibleRows.map((row) => {
                                const draft = Number(row.draftCount) || 0;
                                const reviewed = Number(row.reviewedCount) || 0;
                                const published = Number(row.publishedCount) || 0;
                                const plays = Number(row.playCount) || 0;
                                const low = published < LOW_PUBLISHED;
                                return (
                                    <TableRow key={row.tag} hover>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {row.tag}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">{formatCount(draft)}</TableCell>
                                        <TableCell align="right">{formatCount(reviewed)}</TableCell>
                                        <TableCell align="right">
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                justifyContent="flex-end"
                                                alignItems="center"
                                            >
                                                <span>{formatCount(published)}</span>
                                                {low ? (
                                                    <Chip
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                        label="Low"
                                                    />
                                                ) : null}
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">{formatCount(plays)}</TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {formatDateTime(lastPublishedValue(row))}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                disabled={reviewed < 1}
                                                onClick={() => setPublishTag(row.tag)}
                                                sx={{ textTransform: "none" }}
                                            >
                                                Publish
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={Boolean(publishTag)}
                onClose={closePublishDialog}
                maxWidth={false}
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            width: "96vw",
                            maxWidth: 1400,
                            height: "92vh",
                            maxHeight: "92vh",
                            display: "flex",
                            flexDirection: "column",
                        },
                    },
                }}
            >
                <DialogTitle sx={{ flexShrink: 0 }}>Publish jigsaw</DialogTitle>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                    }}
                >
                    <Typography variant="body2" sx={{ mb: 1.5, flexShrink: 0 }}>
                        Publish 1 REVIEWED image for tag <strong>{publishTag}</strong>?
                        Scroll to zoom, drag to move. The oldest-created puzzle is published first.
                    </Typography>
                    {publishTarget.loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : publishTarget.error ? (
                        <Typography variant="body2" color="text.secondary">
                            {publishTarget.error}
                        </Typography>
                    ) : (
                        <PublishTargetPreview
                            group={publishTarget.group}
                            languageFilter={languageFilter}
                            tag={publishTag}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2, flexShrink: 0 }}>
                    <Button
                        color="error"
                        variant="outlined"
                        startIcon={
                            deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />
                        }
                        disabled={
                            deleting
                            || publishing
                            || publishTarget.loading
                            || !publishStoryId
                        }
                        onClick={() => setDeleteConfirmOpen(true)}
                        sx={{ textTransform: "none", minHeight: 44 }}
                    >
                        {deleting ? "Deleting…" : "Delete"}
                    </Button>
                    <Stack direction="row" spacing={1}>
                        <Button
                            onClick={closePublishDialog}
                            disabled={publishing || deleting}
                            sx={{ minHeight: 44 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handlePublish}
                            disabled={
                                publishing
                                || deleting
                                || publishTarget.loading
                                || Boolean(publishTarget.error)
                            }
                            variant="contained"
                            sx={{ minHeight: 44 }}
                        >
                            {publishing ? "Publishing…" : "Publish"}
                        </Button>
                    </Stack>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={deleteConfirmOpen}
                setOpen={setDeleteConfirmOpen}
                title="Delete jigsaw?"
                message="This permanently deletes this story, its jigsaw puzzles, and the related sliding puzzle. Scheduled jobs will not recreate them. You can then review the next REVIEWED image for this tag."
                targetName={
                    publishTitle
                        ? `'${publishTitle}'`
                        : publishStoryId
                          ? `Story ${publishStoryId}`
                          : "This jigsaw"
                }
                targetId={publishStoryId}
                callback={handleDeleteStory}
                okText="OK"
                cancelText="Cancel"
            />

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}
